// SF6 — Regional Product Index.
//
// Which products sell disproportionately well where, relative to how much the
// seller sells in that region overall. This measures PREFERENCE, not volume: a
// small product can index 3.00 and a bestseller can index 0.90.
//
// Two readings of one matrix, per the PRD — region-first ("what over-indexes
// here") and product-first ("where does this over-index"). Same numbers, pivoted.
//
// Most cells are unremarkable and are styled to recede. The index says a
// pattern exists and never why, and the copy here must not imply otherwise.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../GeographicSales.module.css';
import { getProductIndex } from '../services/geographicSalesApi';
import SyncProgress from '../../components/SyncProgress';

const VIEWS = [
  { key: 'region', label: 'By region' },
  { key: 'product', label: 'By product' },
];

const BY = [
  { key: 'product_type', label: 'Product type' },
  { key: 'asin', label: 'ASIN' },
];

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

// Over- and under-indexing read differently and should look different. The
// neutral middle is deliberately quiet.
const Index = ({ value, notable }) => {
  if (value === null || value === undefined) return <span className={local.blank}>—</span>;
  const tone = !notable ? local.indexFlat
    : value > 1 ? local.indexOver : local.indexUnder;
  return <span className={tone}>{value.toFixed(2)}</span>;
};

const ProductIndex = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [view, setView] = useState('region');
  const [by, setBy] = useState('product_type');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getProductIndex(token, { start, end, by, grain: 'state' }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, by, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  // One grouping pass for both views — the matrix is the same either way, only
  // the key changes, so there is no reason to fetch or compute it twice.
  const groups = useMemo(() => {
    const cells = data?.cells || [];
    const keyed = new Map();
    cells.forEach((cell) => {
      const key = view === 'region' ? cell.region : cell.product;
      if (!keyed.has(key)) keyed.set(key, []);
      keyed.get(key).push(cell);
    });
    // Strongest signal first, so the groups worth reading are at the top.
    return [...keyed.entries()]
      .map(([key, rows]) => ({
        key,
        rows: [...rows].sort((a, b) => b.index - a.index),
        peak: Math.max(...rows.map((r) => Math.abs(r.index - 1))),
      }))
      .sort((a, b) => b.peak - a.peak);
  }, [data, view]);

  const suppressed = data && data.available === false;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What sells differently, and where</h2>
        <div className={local.segmented}>
          {VIEWS.map((option) => (
            <button key={option.key} type="button"
              className={option.key === view ? local.segmentOn : local.segment}
              onClick={() => setView(option.key)}>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <p className={local.muted}>
        An index above 1.00 means the product takes a bigger share of that region
        than it does of your business overall. It shows that a pattern exists,
        never why.
      </p>

      <div className={local.segmented}>
        {BY.map((option) => (
          <button key={option.key} type="button"
            className={option.key === by ? local.segmentOn : local.segment}
            onClick={() => setBy(option.key)}>
            {option.label}
          </button>
        ))}
      </div>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.progress ? 'Sync in progress'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the index'}
            </strong>
            <div>{error.message}</div>
            <SyncProgress progress={error.progress} />
          </div>
        </div>
      )}

      {/* A one-product catalogue indexes 1.00 everywhere by definition. */}
      {!error && suppressed && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div><strong>Not enough of a catalogue to index</strong>
            <div>{data.reason}</div>
          </div>
        </div>
      )}

      {!error && !suppressed && (
        groups.length === 0 ? (
          <div className={local.emptyCell}>
            {loading ? 'Loading…'
              : 'No product sold enough in any one region for an index to mean anything yet.'}
          </div>
        ) : groups.map((group) => (
          <div key={group.key} className={local.indexGroup}>
            <h3 className={local.indexGroupTitle}>{group.key}</h3>
            <div className={local.tableScroll}>
              <table className={local.table}>
                <thead>
                  <tr>
                    <th>{view === 'region' ? 'Product' : 'Region'}</th>
                    <th className={local.numeric}>Units</th>
                    <th className={local.numeric}>
                      {view === 'region' ? 'Share of region' : 'Share of product'}
                    </th>
                    <th className={local.numeric}>Index</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((cell) => (
                    <tr key={`${cell.product}|${cell.region}`}
                      className={cell.notable ? undefined : local.recede}>
                      <td>{view === 'region' ? cell.product : cell.region}</td>
                      <td className={local.numeric}>{inr.format(cell.units)}</td>
                      <td className={local.numeric}>
                        {((view === 'region' ? cell.share_of_region
                          : cell.share_of_product) * 100).toFixed(1)}%
                      </td>
                      <td className={local.numeric}>
                        <Index value={cell.index} notable={cell.notable} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* The floors are part of the reading, not a footnote. */}
      {!error && !suppressed && data?.floors && (
        <div className={local.coverage}>
          Shown where the product sold at least {inr.format(data.floors.cell_units)} units
          in that region, the region sold at least {inr.format(data.floors.region_units)} in
          total, and the product at least {inr.format(data.floors.product_units)} nationally.
          Below any of those the index is left out rather than computed on noise.
          {data.business_units_excluded > 0 && (
            <> {inr.format(data.business_units_excluded)} business-order units are
            excluded from this view.</>
          )}
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ProductIndex;
